/**
 * Shared ECharts styling constants used across all chart components.
 *
 * These are light-mode defaults. `adaptEchartsOptions()` automatically
 * remaps the Tailwind gray hex values to CSS custom properties in dark mode.
 */

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

/** Tailwind gray palette used across all chart chrome (axes, grid, text). */
export const CHART_COLORS = {
  /** Axis lines, tick marks — gray-300 */
  axisLine: '#d1d5db',
  /** Grid / split lines — gray-100 */
  splitLine: '#f3f4f6',
  /** Axis labels, secondary text — gray-500 */
  label: '#6b7280',
  /** Tooltip border — gray-200 */
  tooltipBorder: '#e5e7eb',
  /** Tooltip body text — gray-700 */
  tooltipText: '#374151',
  /** Bold / emphasis text — gray-900 */
  boldText: '#111827',
} as const;

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

/** Standard tooltip container styling shared by all chart types. */
export const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#fff',
  borderColor: CHART_COLORS.tooltipBorder,
  borderWidth: 1,
  extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
  padding: [10, 14],
  textStyle: { fontSize: 12, color: CHART_COLORS.tooltipText },
};

// ---------------------------------------------------------------------------
// Axis styling
// ---------------------------------------------------------------------------

/** Shared axis name text style. */
export const CHART_AXIS_NAME_STYLE = {
  fontSize: 12,
  color: CHART_COLORS.label,
};

/** Standard x-axis styling (category or value). Spread and override as needed. */
export const CHART_X_AXIS_STYLE = {
  nameTextStyle: CHART_AXIS_NAME_STYLE,
  axisTick: { show: false },
  axisLine: { lineStyle: { color: CHART_COLORS.axisLine } },
  splitLine: { lineStyle: { color: CHART_COLORS.splitLine } },
  axisLabel: { fontSize: 12, color: CHART_COLORS.label },
};

/** Standard y-axis styling. Spread and override as needed. */
export const CHART_Y_AXIS_STYLE = {
  nameTextStyle: CHART_AXIS_NAME_STYLE,
  axisTick: { show: false },
  axisLine: { show: false },
  splitLine: { lineStyle: { color: CHART_COLORS.splitLine } },
  axisLabel: { fontSize: 12, color: CHART_COLORS.label },
};

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

/** Standard grid with containLabel — most chart types. */
export const CHART_GRID = {
  top: 40,
  bottom: 25,
  left: 40,
  right: 30,
  containLabel: true,
};

/** Compact grid for smaller / embedded charts. */
export const CHART_GRID_COMPACT = {
  top: 30,
  bottom: 8,
  left: 8,
  right: 8,
  containLabel: true,
};

// ---------------------------------------------------------------------------
// Bar styling
// ---------------------------------------------------------------------------

/** Standard rounded top corners for bar series. */
export const CHART_BAR_BORDER_RADIUS = [4, 4, 0, 0];

// ---------------------------------------------------------------------------
// Tooltip helpers
// ---------------------------------------------------------------------------

/** Wrap a value in bold styling for use inside tooltip HTML. */
export function tooltipBold(v: string | number): string {
  return `<span style="font-weight:600;color:var(--neutral-900)">${v}</span>`;
}

/** Build a single tooltip row with a label and a (bold) value. */
export function tooltipRow(label: string, value: string | number): string {
  return `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:var(--neutral-500)">${label}</span>${tooltipBold(
    value,
  )}</div>`;
}

/** Build a tooltip header. Uses neutral-900 by default for theme-aware text. */
export function tooltipHeader(text: string, color?: string): string {
  return `<div style="margin-bottom:4px;font-weight:600;color:${color ?? 'var(--neutral-900)'}">${text}</div>`;
}
